import { useEffect, useMemo, useState } from 'react';
import { api } from './api.js';
import { stamp } from './format.js';

/**
 * The plan library.
 *
 * Two panes: the plans down the left, the version you are looking at on the right. A plan
 * on its own is not a thing you can edit — a VERSION is — so selecting a plan selects its
 * newest version, and the version is what every button on the right acts on.
 *
 * The screen is built around one rule, and states it rather than hiding it: an approved
 * version cannot be edited. When you are looking at one there are no edit controls at all,
 * only "Start a new version". Greying out a row of buttons would leave somebody clicking
 * them and wondering; removing them and saying why does not.
 */

const KIND_LABEL = {
  base: 'Base plan',
  incident_type: 'Incident type',
  site: 'Site',
};

const KIND_HELP = {
  base: 'Applies to every incident — the things done regardless of what has happened.',
  incident_type: 'Added on top when the incident is of this type.',
  site: 'Added on top for incidents at this location.',
};

const PRIORITY_LABEL = { 1: 'P1', 2: 'P2', 3: 'P3' };


/**
 * Turn a block pasted out of a Word document into one item per line.
 *
 * Word brings its own rubbish with it: non-breaking spaces, smart bullets, tab-indented
 * table cells, and auto-numbering that becomes literal text the moment it leaves Word. All
 * of that has to come off, or every item in the plan starts with "1." and the numbering is
 * wrong the first time somebody reorders anything.
 *
 * What it deliberately does NOT do is decide which lines are worth keeping. A heading, a
 * page number or a stray blank stays in the list with a tick beside it for the person to
 * clear. Guessing wrong and silently dropping a real checklist line is far worse than
 * showing one line too many, because a missing line is invisible.
 */
const MARKER = new RegExp(
  '^\\s*(?:' +
  '[-\u2013\u2014\u2022\u00b7\u25aa\u25e6*]\\s+' +   // - – — • · ▪ ◦ *
  '|o\\s+' +                                              // Word's second-level bullet
  '|\\(?\\d{1,3}[.):]\\s*' +                           // 1.  2)  (3)  4:
  '|\\(?[a-z]\\)\\s+' +                                // a)  (b)
  ')');

// Roman numerals are deliberately NOT stripped. "civil." is entirely i/v/x/l/c/d/m letters
// followed by a full stop, so a roman-numeral rule eats the first word of a real sentence.

export function parsePastedList(raw) {
  return raw
    .replace(/\r\n?/g, '\n')
    .replace(/\u00a0/g, ' ')          // Word's non-breaking space, invisible and everywhere
    .split('\n')
    .map((line) => {
      let text = line.replace(/\t+/g, ' ').trim();
      // Once only. A line that genuinely begins "- 2. something" keeps its "2." rather
      // than having two passes quietly rewrite the person's own wording.
      text = text.replace(MARKER, '').trim();
      return text;
    })
    .filter((text) => text.length > 0)
    .map((text, i) => ({
      key: `${i}-${text.slice(0, 24)}`,
      text,
      priority: 3,
      keep: true,
      tooLong: text.length > 1000,
    }));
}

/** Whichever version the screen should open on: the draft if there is one, else the newest. */
function defaultVersion(plan) {
  if (!plan?.versions?.length) return null;
  return plan.versions.find((v) => v.status === 'draft') ?? plan.versions[0];
}

export function Plans({ session }) {
  const [library, setLibrary] = useState(null);
  const [codes, setCodes] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [version, setVersion] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const mayAuthor = session.permissions.includes('plan.author');
  const mayApprove = session.permissions.includes('plan.approve');

  async function loadLibrary(keepVersionId) {
    try {
      const out = await api.plans();
      setLibrary(out.plans);
      setCodes(out.positionCodes);

      // Keep whatever was open if it still exists; otherwise fall to the first plan. Left
      // to itself, every save would bounce the selection back to the top of the list.
      const plan = out.plans.find((p) => p.id === selectedPlanId) ?? out.plans[0] ?? null;
      setSelectedPlanId(plan?.id ?? null);
      const v = keepVersionId
        ?? (plan?.versions.some((x) => x.id === selectedVersionId) ? selectedVersionId : null)
        ?? defaultVersion(plan)?.id
        ?? null;
      setSelectedVersionId(v);
    } catch {
      setError('Could not load the plan library.');
    }
  }

  useEffect(() => { loadLibrary(); }, []);

  useEffect(() => {
    if (!selectedVersionId) { setVersion(null); return; }
    let stale = false;
    api.planVersion(selectedVersionId)
      .then((out) => { if (!stale) setVersion(out.version); })
      .catch(() => { if (!stale) setError('Could not load that version.'); });
    return () => { stale = true; };
  }, [selectedVersionId]);

  const selectedPlan = library?.find((p) => p.id === selectedPlanId) ?? null;

  /** Runs an action, shows the server's own explanation if it refuses, and reloads. */
  async function run(fn, { reloadVersion = true } = {}) {
    setBusy(true); setError(null);
    try {
      const out = await fn();
      // The server writes these sentences next to the rule they describe, so the screen
      // does not keep its own copy of them to drift out of step.
      await loadLibrary(reloadVersion ? selectedVersionId : undefined);
      if (reloadVersion && selectedVersionId) {
        setVersion((await api.planVersion(selectedVersionId)).version);
      }
      return out;
    } catch (err) {
      setError(err.detail || 'That did not save.');
      return null;
    } finally { setBusy(false); }
  }

  if (!library) {
    return <div className="card"><div className="empty">{error ?? 'Loading…'}</div></div>;
  }

  return (
    <div className="plans">
      <PlanList
        plans={library}
        selectedPlanId={selectedPlanId}
        selectedVersionId={selectedVersionId}
        mayAuthor={mayAuthor}
        onSelect={(planId, versionId) => {
          setSelectedPlanId(planId);
          setSelectedVersionId(versionId ?? defaultVersion(library.find((p) => p.id === planId))?.id ?? null);
          setError(null);
        }}
        onNew={() => setCreating(true)}
      />

      <div className="card planPane">
        {error && <p className="err inline">{error}</p>}

        {!version ? (
          <div className="empty">
            {library.length === 0
              ? (mayAuthor
                  ? 'No plans yet. A plan is a checklist your functions get at the start of an incident — write one here and approve it, and it can be applied to any incident.'
                  : 'No plans have been written yet.')
              : 'Choose a plan on the left.'}
          </div>
        ) : (
          <VersionPane
            plan={selectedPlan}
            version={version}
            codes={codes}
            busy={busy}
            mayAuthor={mayAuthor}
            mayApprove={mayApprove}
            run={run}
            onPickVersion={setSelectedVersionId}
          />
        )}
      </div>

      {creating && (
        <NewPlan
          onClose={() => setCreating(false)}
          onCreated={async (plan) => {
            setCreating(false);
            setSelectedPlanId(plan.id);
            await loadLibrary(plan.versions[0].id);
            setSelectedVersionId(plan.versions[0].id);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- left: the library

function PlanList({ plans, selectedPlanId, selectedVersionId, mayAuthor, onSelect, onNew }) {
  // Grouped by kind, in the order the layers are applied: base first, then incident type,
  // then site. That is the order they land on an incident, so it is the order they are read.
  const groups = ['base', 'incident_type', 'site']
    .map((kind) => ({ kind, plans: plans.filter((p) => p.kind === kind) }))
    .filter((g) => g.plans.length);

  return (
    <div className="card planList">
      <h2>
        Plans
        {mayAuthor && <button className="hoBtn head" onClick={onNew}>New plan</button>}
      </h2>
      <div className="body">
        {groups.length === 0 && <div className="empty">Nothing here yet.</div>}
        {groups.map((g) => (
          <div key={g.kind} className="planGroup">
            <h3 className="sectionHead">{KIND_LABEL[g.kind]}</h3>
            {g.plans.map((p) => {
              const on = p.id === selectedPlanId;
              return (
                <div key={p.id} className={`planRow ${on ? 'on' : ''}`}>
                  <button className="planName" onClick={() => onSelect(p.id)}>
                    <span className="nm">{p.name}</span>
                    <span className="cd">{p.code}</span>
                  </button>
                  {/* Versions are only listed for the plan you are on. All of them, all the
                      time, is a wall of numbers nobody reads. */}
                  {on && (
                    <div className="vList">
                      {p.versions.map((v) => (
                        <button key={v.id}
                                className={`vRow ${v.id === selectedVersionId ? 'on' : ''} ${v.status}`}
                                onClick={() => onSelect(p.id, v.id)}>
                          <span className="vNum">v{v.version}</span>
                          <span className={`pill ${v.status}`}>{v.status}</span>
                          <span className="vMeta">
                            {v.items} item{v.items === 1 ? '' : 's'}
                            {v.used_on > 0 && ` · used on ${v.used_on}`}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- right: one version

function VersionPane({ plan, version, codes, busy, mayAuthor, mayApprove, run, onPickVersion }) {
  const editable = version.status === 'draft' && mayAuthor;
  const [adding, setAdding] = useState(null);      // position code the add row is open for
  const [pasting, setPasting] = useState(null);   // position code the paste box is open for

  // Items grouped by function, each in its own sort order. useMemo because this runs on
  // every keystroke in the add row otherwise.
  const byCode = useMemo(() => {
    const map = new Map();
    for (const item of version.items) {
      if (!map.has(item.position_code)) map.set(item.position_code, []);
      map.get(item.position_code).push(item);
    }
    for (const list of map.values()) list.sort((a, b) => a.sort - b.sort || a.id.localeCompare(b.id));
    return map;
  }, [version.items]);

  // Functions that have items, then any known code that does not yet — so writing the
  // first item for a function does not require typing its code from memory.
  const codesInUse = [...byCode.keys()];
  const spare = codes.map((c) => c.code).filter((c) => !codesInUse.includes(c));

  async function move(item, list, delta) {
    const i = list.indexOf(item);
    const j = i + delta;
    if (j < 0 || j >= list.length) return;
    const ids = list.map((x) => x.id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await run(() => api.reorderPlanItems(version.id, ids));
  }

  return (
    <>
      <div className="planHead">
        <div>
          <h2>{plan?.name}</h2>
          <p className="sub">
            {KIND_LABEL[version.kind]} · {version.code} · v{version.version}
            <span className={`pill ${version.status}`}>{version.status}</span>
          </p>
          {plan?.description && <p className="sub2">{plan.description}</p>}
        </div>
        <div className="spacer" />
        <div className="planActions">
          {version.status === 'draft' && mayApprove && (
            <button className="hoBtn primary" disabled={busy || version.items.length === 0}
                    title={version.items.length === 0
                      ? 'Add at least one item before approving'
                      : 'Freeze this version so it can be used on an incident'}
                    onClick={() => run(() => api.approvePlanVersion(version.id))}>
              Approve v{version.version}
            </button>
          )}
          {version.status === 'approved' && mayAuthor && (
            <button className="hoBtn" disabled={busy}
                    onClick={async () => {
                      const out = await run(() => api.startDraft(plan.id, version.id),
                        { reloadVersion: false });
                      if (out?.version) onPickVersion(out.version.id);
                    }}>
              Start a new version
            </button>
          )}
          {version.status === 'approved' && mayApprove && (
            <button className="hoBtn" disabled={busy}
                    title="Take it out of the list offered for new incidents. Incidents that ran on it keep their record."
                    onClick={() => run(() => api.retirePlanVersion(version.id))}>
              Retire
            </button>
          )}
        </div>
      </div>

      {/* Said plainly at the top rather than discovered by clicking. */}
      {version.status !== 'draft' && (
        <p className="frozenNote">
          {version.status === 'approved'
            ? 'Approved and frozen. Its items cannot be changed — start a new version to revise them.'
            : 'Retired. It is no longer offered for new incidents, and the incidents that ran on it still resolve to it.'}
          {version.approved_at && ` Approved ${stamp(version.approved_at)}`}
          {version.approved_by_name && ` by ${version.approved_by_name}`}
          {version.used_on > 0 && ` · used on ${version.used_on} incident${version.used_on === 1 ? '' : 's'}`}.
        </p>
      )}

      <div className="body">
        {version.items.length === 0 && (
          <div className="empty">
            Nothing in this version yet.
            {editable && ' Add the first item against a function below.'}
          </div>
        )}

        {codesInUse.map((code) => {
          const list = byCode.get(code);
          return (
            <div key={code} className="fnBlock">
              <h3 className="sectionHead">
                {codes.find((c) => c.code === code)?.name ?? code}
                <span className="cd">{code}</span>
                <span className="count">{list.length}</span>
              </h3>
              <ul className="planItems">
                {list.map((item) => (
                  <PlanItem key={item.id} item={item} list={list} editable={editable}
                            busy={busy} versionId={version.id} run={run} onMove={move} />
                ))}
              </ul>
              {editable && pasting === code && (
                <PasteList code={code} versionId={version.id} run={run}
                           onDone={() => setPasting(null)} />
              )}
              {editable && adding === code && (
                <AddItem code={code} versionId={version.id} run={run}
                         onDone={() => setAdding(null)} />
              )}
              {editable && adding !== code && pasting !== code && (
                <div className="addRow">
                  <button className="hoBtn small"
                          onClick={() => { setPasting(null); setAdding(code); }}>
                    Add an item to {code}
                  </button>
                  <button className="hoBtn small"
                          onClick={() => { setAdding(null); setPasting(code); }}>
                    Paste a list
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {editable && spare.length > 0 && (
          <div className="fnBlock spare">
            <h3 className="sectionHead">Add a function</h3>
            <div className="codeChips">
              {spare.map((c) => (
                <button key={c} className="codeChip" onClick={() => setAdding(c)}>
                  {codes.find((x) => x.code === c)?.name ?? c}
                  <span className="cd">{c}</span>
                </button>
              ))}
            </div>
            {adding && !codesInUse.includes(adding) && (
              <>
                <AddItem code={adding} versionId={version.id} run={run}
                         onDone={() => setAdding(null)} />
                <button className="hoBtn small"
                        onClick={() => { const c = adding; setAdding(null); setPasting(c); }}>
                  Paste a list into {adding} instead
                </button>
              </>
            )}
            {pasting && !codesInUse.includes(pasting) && (
              <PasteList code={pasting} versionId={version.id} run={run}
                         onDone={() => setPasting(null)} />
            )}
          </div>
        )}
      </div>
    </>
  );
}

function PlanItem({ item, list, editable, busy, versionId, run, onMove }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const [priority, setPriority] = useState(item.priority);

  if (editing) {
    return (
      <li className="planItem editing">
        <form onSubmit={async (e) => {
          e.preventDefault();
          if (!text.trim()) return;
          const ok = await run(() => api.updatePlanItem(versionId, item.id,
            { text: text.trim(), priority: Number(priority) }));
          if (ok) setEditing(false);
        }}>
          <select value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value={1}>P1</option><option value={2}>P2</option><option value={3}>P3</option>
          </select>
          <input autoFocus value={text} onChange={(e) => setText(e.target.value)} />
          <button type="submit" disabled={busy || !text.trim()}>Save</button>
          <button type="button" className="ghost"
                  onClick={() => { setText(item.text); setPriority(item.priority); setEditing(false); }}>
            Cancel
          </button>
        </form>
      </li>
    );
  }

  return (
    <li className="planItem">
      <span className={`pri p${item.priority}`}>{PRIORITY_LABEL[item.priority]}</span>
      <span className="txt">{item.text}</span>
      {editable && (
        <span className="rowActions">
          <button className="iconBtn" title="Move up" disabled={busy || list[0] === item}
                  onClick={() => onMove(item, list, -1)}>↑</button>
          <button className="iconBtn" title="Move down" disabled={busy || list[list.length - 1] === item}
                  onClick={() => onMove(item, list, 1)}>↓</button>
          <button className="hoBtn small" onClick={() => setEditing(true)}>Edit</button>
          <button className="hoBtn small danger" disabled={busy}
                  onClick={() => run(() => api.deletePlanItem(versionId, item.id))}>Remove</button>
        </span>
      )}
    </li>
  );
}

/**
 * Paste a checklist straight out of a Word document.
 *
 * The preview is the whole point. Parsing somebody's document is guesswork however careful
 * the rules are, so the guess is shown BEFORE anything is saved: every line, with a tick to
 * drop the ones that are headings or page furniture, and a priority on each. Adding without
 * showing what was understood would mean discovering the mistake as thirty rows already in
 * the plan, and then deleting them one at a time.
 */
function PasteList({ code, versionId, run, onDone }) {
  const [raw, setRaw] = useState('');
  const [lines, setLines] = useState(null);
  const [defaultPriority, setDefaultPriority] = useState(3);
  const [saving, setSaving] = useState(false);

  const kept = lines?.filter((l) => l.keep && !l.tooLong) ?? [];

  function parse(text) {
    setRaw(text);
    const parsed = parsePastedList(text);
    setLines(parsed.map((l) => ({ ...l, priority: defaultPriority })));
  }

  function setAllPriorities(p) {
    setDefaultPriority(p);
    setLines((ls) => ls?.map((l) => ({ ...l, priority: p })) ?? null);
  }

  if (!lines) {
    return (
      <div className="pasteBox">
        <label className="f">
          <span>Paste your list — one action per line</span>
          <textarea autoFocus rows={6} value={raw}
                    onChange={(e) => setRaw(e.target.value)}
                    onPaste={(e) => {
                      // Read the clipboard directly so the preview appears on the paste
                      // itself rather than after a second click on a button nobody expects.
                      const text = e.clipboardData?.getData('text/plain');
                      if (text) { e.preventDefault(); parse(text); }
                    }}
                    placeholder={'Copy the list out of your Word document and paste it here.\n\nNumbering and bullets are removed automatically. You get to check every line before anything is saved.'} />
        </label>
        <div className="inlineAdd">
          <button type="button" disabled={!raw.trim()} onClick={() => parse(raw)}>
            Read the list
          </button>
          <button type="button" className="ghost" onClick={onDone}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pasteBox">
      <div className="pasteHead">
        <b>{lines.length} line{lines.length === 1 ? '' : 's'} read</b>
        <span className="hint">
          Untick anything that is not an action — a heading, a page number, a stray line.
        </span>
        <div className="spacer" />
        <label className="allPri">
          Priority for all
          <select value={defaultPriority} onChange={(e) => setAllPriorities(Number(e.target.value))}>
            <option value={1}>P1</option><option value={2}>P2</option><option value={3}>P3</option>
          </select>
        </label>
      </div>

      <ul className="pasteLines">
        {lines.map((l, i) => (
          <li key={l.key} className={l.keep && !l.tooLong ? '' : 'off'}>
            <input type="checkbox" checked={l.keep && !l.tooLong} disabled={l.tooLong}
                   onChange={(e) => setLines((ls) =>
                     ls.map((x, j) => (j === i ? { ...x, keep: e.target.checked } : x)))} />
            <select value={l.priority} disabled={!l.keep || l.tooLong}
                    onChange={(e) => setLines((ls) =>
                      ls.map((x, j) => (j === i ? { ...x, priority: Number(e.target.value) } : x)))}>
              <option value={1}>P1</option><option value={2}>P2</option><option value={3}>P3</option>
            </select>
            <span className="txt">{l.text}</span>
            {l.tooLong && <span className="warn">too long — shorten it first</span>}
          </li>
        ))}
      </ul>

      <div className="inlineAdd">
        <button type="button" disabled={saving || kept.length === 0}
                onClick={async () => {
                  setSaving(true);
                  const ok = await run(() => api.addPlanItems(versionId, {
                    positionCode: code,
                    items: kept.map((l) => ({ text: l.text, priority: l.priority })),
                  }));
                  setSaving(false);
                  if (ok) onDone();
                }}>
          {saving ? 'Adding…' : `Add ${kept.length} item${kept.length === 1 ? '' : 's'} to ${code}`}
        </button>
        <button type="button" className="ghost" onClick={() => { setLines(null); }}>
          Back
        </button>
        <button type="button" className="ghost" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

function AddItem({ code, versionId, run, onDone }) {
  const [text, setText] = useState('');
  const [priority, setPriority] = useState(3);
  const [saving, setSaving] = useState(false);

  return (
    <form className="inlineAdd" onSubmit={async (e) => {
      e.preventDefault();
      if (!text.trim() || saving) return;
      setSaving(true);
      const ok = await run(() => api.addPlanItem(versionId, {
        positionCode: code, text: text.trim(), priority: Number(priority),
      }));
      setSaving(false);
      // Stays open on success. Somebody writing a checklist writes eight lines, not one,
      // and closing the form after each is eight extra clicks.
      if (ok) setText('');
    }}>
      <select value={priority} onChange={(e) => setPriority(e.target.value)}>
        <option value={1}>P1</option><option value={2}>P2</option><option value={3}>P3</option>
      </select>
      <input autoFocus value={text} onChange={(e) => setText(e.target.value)}
             placeholder={`What ${code} does — one action per line`} />
      <button type="submit" disabled={saving || !text.trim()}>Add</button>
      <button type="button" className="ghost" onClick={onDone}>Done</button>
    </form>
  );
}

// ---------------------------------------------------------------- new plan

function NewPlan({ onClose, onCreated }) {
  const [kind, setKind] = useState('incident_type');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Whether the code has been typed by hand. Until it has, it follows the name — and once
  // it has it must stop, or the person's own code is overwritten as they keep typing.
  const [codeTouched, setCodeTouched] = useState(false);

  const suggest = (v) => v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const { plan } = await api.createPlan({
        kind, name: name.trim(),
        code: (codeTouched ? code : suggest(name)).trim(),
        description,
      });
      onCreated(plan);
    } catch (err) {
      setError(err.detail || 'That plan could not be created.');
    } finally { setBusy(false); }
  }

  return (
    <div className="hoWrap" onClick={onClose}>
      <div className="ho" onClick={(e) => e.stopPropagation()}>
        <div className="hoHead">
          <div>
            <h3>New plan</h3>
            <p>It opens as a draft. Nothing can use it until it is approved.</p>
          </div>
          <button className="hoX" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form className="hoBody form" onSubmit={submit}>
          <label className="f">
            <span>What kind</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="base">Base plan</option>
              <option value="incident_type">Incident type</option>
              <option value="site">Site</option>
            </select>
            <small>{KIND_HELP[kind]}</small>
          </label>

          <label className="f">
            <span>Name</span>
            <input autoFocus value={name} maxLength={200} required
                   onChange={(e) => setName(e.target.value)}
                   placeholder="Crude oil storage tank fire" />
          </label>

          <label className="f">
            <span>Short code</span>
            <input value={codeTouched ? code : suggest(name)} maxLength={40}
                   onChange={(e) => { setCodeTouched(true); setCode(e.target.value); }}
                   placeholder="tank-fire" />
            <small>Identifies the plan. Letters, numbers and dashes.</small>
          </label>

          <label className="f">
            <span>What it is for (optional)</span>
            <textarea rows={2} maxLength={2000} value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="When this plan should be applied" />
          </label>

          {error && <p className="err">{error}</p>}

          <div className="actions">
            <span className="spacer" />
            <button type="button" className="ghost" onClick={onClose}>Cancel</button>
            <button className="btn narrow" type="submit" disabled={busy || !name.trim()}>
              {busy ? 'Creating…' : 'Create plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
